import React, { useEffect, useMemo, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "motion/react";
import { Link, useSearchParams } from "react-router-dom";
import { blogs } from "./blogData";
import { Helmet } from "react-helmet";
import { ChevronLeft, ChevronRight } from "lucide-react";

const POSTS_PER_PAGE = 12;

// Page numbers to show: first, last and the current page's neighbours, with
// "..." for gaps (1 2 ... 6, 1 ... 4 5 6 ... 10). A gap that would hide only
// one page shows that page instead.
const pageItems = (current, total) => {
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const items = [];
  sorted.forEach((p, i) => {
    const prev = sorted[i - 1];
    if (i > 0 && p - prev === 2) items.push(prev + 1);
    else if (i > 0 && p - prev > 2) items.push(`gap-${p}`);
    items.push(p);
  });
  return items;
};

const Blog = () => {
  // Category and page live in the URL (/blog?category=Blogs&page=2) so the back
  // button, shared links and search engines all land on the same page.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get("category") || "All Posts";
  const gridRef = useRef(null);
  const isFirstRender = useRef(true);

  const categories = useMemo(() => {
    const counts = blogs.reduce((acc, item) => {
      const normalized = item.category.trim();
      acc[normalized] = (acc[normalized] || 0) + 1;
      return acc;
    }, {});

    return [
      { name: "All Posts", count: blogs.length },
      ...Object.entries(counts).map(([name, count]) => ({ name, count })),
    ];
  }, []);

  const normalize = (value) => value.trim().toLowerCase();

  const filteredBlogs =
    selectedCategory === "All Posts"
      ? blogs
      : blogs.filter(
          (post) => normalize(post.category) === normalize(selectedCategory),
        );

  const totalPages = Math.max(1, Math.ceil(filteredBlogs.length / POSTS_PER_PAGE));
  const requestedPage = parseInt(searchParams.get("page"), 10) || 1;
  const currentPage = Math.min(Math.max(requestedPage, 1), totalPages);
  const firstIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const pageBlogs = filteredBlogs.slice(firstIndex, firstIndex + POSTS_PER_PAGE);

  const updateParams = (category, page) => {
    const next = {};
    if (category !== "All Posts") next.category = category;
    if (page > 1) next.page = String(page);
    setSearchParams(next);
  };

  const goToPage = (page) => updateParams(selectedCategory, page);

  // Bring the top of the grid into view when the page or category changes
  // (not on the first load, so arriving at /blog starts at the hero).
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [currentPage, selectedCategory]);

  return (
    <>
      <Helmet>
        <title>AI, Salesforce & Data Blog | Martech Insights</title>

        <meta
          name="description"
          content="Explore AI, Salesforce & data analytics blogs by Comsense. Get insights on Martech, automation, CX & digital transformation trends."
        />

        <meta
          name="keywords"
          content="AI blog India, marketing automation insights, data analytics blog India"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Comsense Technologies" />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="AI, Salesforce & Data Blog India | Martech Insights by Comsense"
        />
        <meta
          property="og:description"
          content="Latest blogs on AI, Salesforce, Martech & data analytics trends."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://comsensetechnologies.com/blog"
        />
      </Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@100..900&display=swap');
        .blog-page { font-family: 'Outfit', sans-serif; background-color: #f8fafc; }
        .blog-page * { font-family: 'Outfit', sans-serif; }

        .blog-hero {
          padding: 100px 0 100px;
          background: #e2f1fe;
          color: #fff;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .hero-title-container {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          text-align: left;
          max-width: 600px;
        }

        .blog-hero h1 {
          font-weight: 800;
          margin-bottom: 10px;
          font-size: 42px;
          text-align: left;
          color: #0f172a;
          letter-spacing: -.5px;
        }

        .blog-hero p {
          font-size: 16px;
          color: #475569;
          text-align: left;
          max-width: 900px;
          margin-bottom: 0px;
        }

        .hero-monitor-icon {
          position: absolute;
          top: 50%;
          right: 10%;
          transform: translateY(-50%);
          opacity: 0.2;
          pointer-events: none;
        }

        .blog-filter {
          padding: 60px 0 40px;
          display: flex;
          justify-content: flex-start;
          gap: 10px;
          flex-wrap: wrap;
          border-bottom: 1px solid #e2e8f0;
          margin-bottom: 40px;
        }

        .filter-label {
          font-weight: 700;
          margin-right: 20px;
          display: flex;
          align-items: center;
          color: #0f172a;
        }

        .filter-btn {
          padding: 8px 20px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: #fff;
          color: #64748b;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .filter-btn:hover, .filter-btn.active {
          background: #e00;
          color: #fff;
          border-color: #e00;
        }

        .blog-card {
          background: #fff;
          border-radius: 0;
          border: none;
          overflow: hidden;
          transition: all 0.4s ease;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .blog-card:hover {
          transform: translateY(-5px);
        }

        .blog-card-img-wrapper {
          position: relative;
          height: 200px;
          overflow: hidden;
          margin-bottom: 20px;
        }

        .blog-card-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .blog-card-body {
          padding: 10px;
          flex-grow: 1;
          display: flex;
          flex-direction: column;
        }

        .blog-card-meta {
          font-size: 12px;
          font-weight: 700;
          color: #ee0000;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 10px;
        }

        .blog-card-author {
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          margin-bottom: 12px;
        }

        .blog-card-title {
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 15px;
          line-height: 1.4;
        }

        .blog-card-footer {
          margin-top: auto;
          padding-top: 15px;
          margin-left:10px;
        }
.blog-card-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 15px;
  line-height: 1.4;

  display: -webkit-box;
  -webkit-line-clamp: 2;   /* 👈 max 2 lines */
  -webkit-box-orient: vertical;
  overflow: hidden;
}
  .blog-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.blog-card-body {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.blog-card-footer {
  margin-top: auto;
}.blog-card-body {
  min-height: 120px;
}
        .blog-filter { scroll-margin-top: 100px; }

        .blog-pagination {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 20px 0 80px;
        }

        .pagination-controls {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          padding: 5px;
          background: #fff;
          border: 1px solid #edf1f6;
          border-radius: 999px;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03), 0 6px 20px rgba(15, 23, 42, 0.05);
          max-width: 100%;
        }

        .page-btn, .page-nav {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 38px;
          border: none;
          border-radius: 999px;
          background: transparent;
          color: #475569;
          font-weight: 500;
          font-size: 15px;
          line-height: 1;
          cursor: pointer;
          transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
        }

        .page-btn {
          min-width: 38px;
          padding: 0 4px;
          font-variant-numeric: tabular-nums;
        }

        .page-nav {
          gap: 6px;
          padding: 0 14px;
          color: #0f172a;
          font-weight: 600;
          font-size: 14px;
        }

        .page-btn:hover:not(.active), .page-nav:hover:not(:disabled) {
          background: #fef2f2;
          color: #e00;
        }

        .page-btn.active {
          background: linear-gradient(180deg, #ff2a2a 0%, #e00 100%);
          color: #fff;
          font-weight: 700;
          box-shadow: 0 4px 10px rgba(238, 0, 0, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.18);
          cursor: default;
        }

        .page-btn:active:not(.active), .page-nav:active:not(:disabled) {
          transform: scale(0.95);
        }

        .page-nav:disabled {
          color: #b6c0cd;
          cursor: not-allowed;
        }

        .page-btn:focus-visible, .page-nav:focus-visible {
          outline: 2px solid #e00;
          outline-offset: 2px;
        }

        .page-divider {
          width: 1px;
          height: 18px;
          background: #e5e9f0;
          margin: 0 6px;
        }

        .page-gap {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 26px;
          height: 38px;
          color: #a3aebd;
          font-size: 15px;
          user-select: none;
        }

        .pagination-summary {
          margin: 0;
          font-size: 13px;
          color: #94a3b8;
          letter-spacing: 0.01em;
        }

        .pagination-summary strong {
          color: #475569;
          font-weight: 600;
        }

        @media (max-width: 575px) {
          .pagination-controls { padding: 4px; }
          .page-btn, .page-gap { min-width: 34px; height: 34px; font-size: 14px; }
          .page-nav { height: 34px; padding: 0 9px; }
          .page-nav-label { display: none; }
          .page-divider { margin: 0 3px; }
        }

        .blog-link {
          font-size: 12px;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 5px;
        }
      `}</style>

      <div className="blog-page">
        <section className="blog-hero">
          <Container>
            <motion.div
              className="hero-title-container"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>Blogs</h1>
              <p>Our Chain Of Thoughts For Real-World Outcomes</p>
            </motion.div>
            <div className="hero-monitor-icon">
              <svg
                width="200"
                height="200"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                <line x1="8" y1="21" x2="16" y2="21"></line>
                <line x1="12" y1="17" x2="12" y2="21"></line>
              </svg>
            </div>
          </Container>
        </section>

        <Container>
          <div className="blog-filter" ref={gridRef}>
            <div className="filter-label">Filter by Category:</div>
            {categories.map((category) => (
              <button
                key={category.name}
                className={`filter-btn ${
                  selectedCategory === category.name ? "active" : ""
                }`}
                onClick={() => updateParams(category.name, 1)}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>

          <Row className="g-5 pb-5">
            {pageBlogs.map((blog, idx) => (
              <Col key={blog.id} lg={4} md={6}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <div className="blog-card">
                    <div className="blog-card-img-wrapper">
                      <img
                        src={blog.image}
                        alt={blog.title}
                        className="blog-card-img"
                        loading="lazy"
                      />
                    </div>
                    <div className="blog-card-body">
                      <div className="blog-card-meta">
                        {blog.category}
                        {/* | {blog.date} */}
                      </div>
                      <Link
                        to={`/blog/${
                          blog.slug ||
                          blog.title
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")
                            .replace(/(^-|-$)/g, "")
                        }`}
                        className="blog-card-title"
                        style={{ textDecoration: "none", color: "inherit" }}
                        title={blog.title}
                      >
                        {blog.title}
                      </Link>
                    </div>
                    <div className="blog-card-footer">
                      <div className="blog-card-author">By {blog.author}</div>
                    </div>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>

          {totalPages > 1 && (
            <div className="blog-pagination" role="navigation" aria-label="Blog pages">
              <div className="pagination-controls">
                <button
                  className="page-nav"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={18} strokeWidth={2.25} />
                  <span className="page-nav-label">Prev</span>
                </button>
                <div className="page-divider" aria-hidden="true" />
                {pageItems(currentPage, totalPages).map((item) =>
                  typeof item === "number" ? (
                    <button
                      key={item}
                      className={`page-btn ${item === currentPage ? "active" : ""}`}
                      onClick={() => goToPage(item)}
                      aria-label={`Page ${item}`}
                      aria-current={item === currentPage ? "page" : undefined}
                    >
                      {item}
                    </button>
                  ) : (
                    <span key={item} className="page-gap" aria-hidden="true">
                      &hellip;
                    </span>
                  ),
                )}
                <div className="page-divider" aria-hidden="true" />
                <button
                  className="page-nav"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <span className="page-nav-label">Next</span>
                  <ChevronRight size={18} strokeWidth={2.25} />
                </button>
              </div>
              <p className="pagination-summary">
                Showing <strong>{firstIndex + 1}–{firstIndex + pageBlogs.length}</strong>{" "}
                of <strong>{filteredBlogs.length}</strong> posts
              </p>
            </div>
          )}
        </Container>
      </div>
    </>
  );
};

export default Blog;
